<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { required, email } from "vuelidate/lib/validators";
import { leadMethods, usersMethods, crmOptionMethods } from "@/state/helpers";
import { CRM_OPTION_MODULE } from "@/state/modules/crmOption";
import crmFormMixin from "@/mixins/crmForm.mixin";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

export default {
  page() {
    return { title: this.isEdit ? i18n.t("lead.edit") : i18n.t("lead.create") };
  },
  components: { Layout, PageHeader, Multiselect },
  mixins: [crmFormMixin],
  data() {
    return {
      form: {
        company_name: "",
        contact_first_name: "",
        contact_last_name: "",
        phone: "",
        email: "",
        website: "",
        source: null,
        source_reference: "",
        status: "new",
        assigned_to: null,
        industry: null,
        address: "",
        city: "",
        state: "",
        country: "",
        notes: "",
      },
      assignedUser: null,
    };
  },
  validations: {
    form: {
      contact_first_name: { required },
      contact_last_name: { required },
      // Either phone or email — one contactable channel is enough.
      phone: {
        requiredEither(value) {
          return !!(value && value.trim()) || !!(this.form.email && this.form.email.trim());
        },
      },
      email: {
        email,
        requiredEither(value) {
          return !!(value && value.trim()) || !!(this.form.phone && this.form.phone.trim());
        },
      },
      // leads.source is NOT NULL, and it is the only record of which channel
      // brought this lead in — a blank one used to surface as a raw SQL error.
      source: { required },
    },
  },
  computed: {
    isEdit() {
      return !!this.$route.params.uuid;
    },
    title() {
      return this.isEdit ? i18n.t("lead.edit") : i18n.t("lead.create");
    },
    items() {
      return [
        { text: i18n.t("module.crm"), href: "/" },
        { text: i18n.t("lead.title"), href: "/leads" },
        { text: this.title, active: true },
      ];
    },
    userOptions() {
      return (this.$store.state.users.list.data || []).map((u) => ({ uuid: u.id, name: u.name }));
    },
    // All three come from /v1/crm/options, so adding a status or an industry is
    // a configuration change rather than a frontend deploy.
    statusOptions() {
      return this.$store.getters[`${CRM_OPTION_MODULE}/selectable`]("lead_statuses");
    },
    sourceOptions() {
      return this.$store.getters[`${CRM_OPTION_MODULE}/group`]("lead_sources");
    },
    industryOptions() {
      return this.$store.getters[`${CRM_OPTION_MODULE}/group`]("industries");
    },
    selectedStatus() {
      return this.statusOptions.find((o) => o.value === this.form.status) || null;
    },
    selectedSource() {
      return this.sourceOptions.find((o) => o.value === this.form.source) || null;
    },
    selectedIndustry() {
      return this.industryOptions.find((o) => o.value === this.form.industry) || null;
    },
  },
  async created() {
    await Promise.all([
      this.getAllUser({ filter: { pageNumber: 1, pageSize: 1000 } }),
      this.getCrmOptions(),
    ]);

    if (this.isEdit) {
      const r = await this.getLead({ uuid: this.$route.params.uuid });
      const d = r?.data || {};
      Object.keys(this.form).forEach((k) => {
        if (d[k] !== undefined && d[k] !== null) this.form[k] = d[k];
      });
      // Rows created before the first/last split only carry contact_person.
      if (!d.contact_first_name && d.contact_person) {
        const parts = String(d.contact_person).trim().split(/\s+/);
        this.form.contact_first_name = parts.shift() || "";
        this.form.contact_last_name = parts.join(" ");
      }
      // converted / lost are set by their own endpoints, never by this form.
      if (!this.statusOptions.some((o) => o.value === this.form.status)) {
        this.form.status = "new";
      }
      this.assignedUser = this.userOptions.find((u) => u.uuid === this.form.assigned_to) || null;
    }
  },
  methods: {
    ...leadMethods,
    ...usersMethods,
    ...crmOptionMethods,
    // Filling either half clears the pair's error on both inputs.
    onEitherInput() {
      this.onFieldInput("phone");
      this.onFieldInput("email");
    },
    onAssigneeChange(u) {
      this.assignedUser = u;
      this.form.assigned_to = u ? u.uuid : null;
    },
    onPick(field, option) {
      this.form[field] = option ? option.value : null;
      this.onFieldInput(field);
    },
    async submit() {
      const payload = { ...this.form };

      const result = await this.submitForm(
        () =>
          this.isEdit
            ? this.updateLead({ uuid: this.$route.params.uuid, payload })
            : this.createLead({ payload }),
        {
          successMessage: this.isEdit
            ? this.$t("message.updateXSuccess", [this.$t("lead.title")])
            : this.$t("message.createXSuccess", [this.$t("lead.title")]),
          errorContext: () => this.$store.state.lead.lastError,
        }
      );

      if (result) this.$router.push({ name: "Lead-listing" });
    },
    cancel() {
      this.$router.push({ name: "Lead-listing" });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-form @submit.prevent="submit">
              <b-row>
                <b-col cols="12">
                  <b-form-group :label="$t('lead.companyName')">
                    <b-form-input
                      v-model="form.company_name"
                      :class="{ 'is-invalid': isInvalid('company_name') }"
                      @input="onFieldInput('company_name')"
                    />
                    <div v-if="fieldError('company_name', $t('lead.companyName'))" class="invalid-feedback d-block">
                      {{ fieldError('company_name', $t('lead.companyName')) }}
                    </div>
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('contact.firstName') + ' *'">
                    <b-form-input
                      v-model="form.contact_first_name"
                      :class="{ 'is-invalid': isInvalid('contact_first_name') }"
                      @input="onFieldInput('contact_first_name')"
                    />
                    <div v-if="fieldError('contact_first_name', $t('contact.firstName'))" class="invalid-feedback d-block">
                      {{ fieldError('contact_first_name', $t('contact.firstName')) }}
                    </div>
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('contact.lastName') + ' *'">
                    <b-form-input
                      v-model="form.contact_last_name"
                      :class="{ 'is-invalid': isInvalid('contact_last_name') }"
                      @input="onFieldInput('contact_last_name')"
                    />
                    <div v-if="fieldError('contact_last_name', $t('contact.lastName'))" class="invalid-feedback d-block">
                      {{ fieldError('contact_last_name', $t('contact.lastName')) }}
                    </div>
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('lead.phone')">
                    <vue-tel-input
                      v-model="form.phone"
                      mode="international"
                      default-country="MY"
                      :input-options="{ placeholder: '+60 12-345 6789', styleClasses: 'form-control' }"
                      valid-characters-only
                      class="tel-input-wrap"
                      :class="{ 'tel-invalid': isInvalid('phone') }"
                      @input="onEitherInput"
                    />
                    <div v-if="fieldError('phone', $t('lead.phone'))" class="invalid-feedback d-block">
                      {{ fieldError('phone', $t('lead.phone')) }}
                    </div>
                    <small v-else class="text-muted">{{ $t('message.requiredPhoneOrEmail') }}</small>
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('label.email')">
                    <b-form-input
                      v-model="form.email"
                      type="email"
                      :class="{ 'is-invalid': isInvalid('email') }"
                      @input="onEitherInput"
                    />
                    <div v-if="fieldError('email', $t('label.email'))" class="invalid-feedback d-block">
                      {{ fieldError('email', $t('label.email')) }}
                    </div>
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('lead.source') + ' *'">
                    <multiselect
                      :value="selectedSource"
                      :options="sourceOptions"
                      track-by="value"
                      label="label"
                      :show-labels="false"
                      open-direction="below"
                      :class="{ 'ms-invalid': isInvalid('source') }"
                      :placeholder="$t('label.select')"
                      @input="(o) => onPick('source', o)"
                    />
                    <div v-if="fieldError('source', $t('lead.source'))" class="invalid-feedback d-block">
                      {{ fieldError('source', $t('lead.source')) }}
                    </div>
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('lead.industry')">
                    <multiselect
                      :value="selectedIndustry"
                      :options="industryOptions"
                      track-by="value"
                      label="label"
                      :show-labels="false"
                      open-direction="below"
                      :class="{ 'ms-invalid': isInvalid('industry') }"
                      :placeholder="$t('label.select')"
                      @input="(o) => onPick('industry', o)"
                    />
                    <div v-if="fieldError('industry', $t('lead.industry'))" class="invalid-feedback d-block">
                      {{ fieldError('industry', $t('lead.industry')) }}
                    </div>
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('label.status')">
                    <multiselect
                      :value="selectedStatus"
                      :options="statusOptions"
                      track-by="value"
                      label="label"
                      :show-labels="false"
                      :allow-empty="false"
                      open-direction="below"
                      @input="(o) => onPick('status', o)"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('lead.assignee')">
                    <multiselect
                      :value="assignedUser"
                      :options="userOptions"
                      track-by="uuid"
                      label="name"
                      :show-labels="false"
                      :allow-empty="true"
                      open-direction="below"
                      :placeholder="$t('label.select')"
                      @input="onAssigneeChange"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('lead.website')">
                    <b-form-input v-model="form.website" />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('lead.address')">
                    <b-form-textarea v-model="form.address" rows="2" />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('lead.city')">
                    <b-form-input v-model="form.city" />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('lead.state')">
                    <b-form-input v-model="form.state" />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('lead.country')">
                    <b-form-input v-model="form.country" />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('lead.notes')">
                    <b-form-textarea v-model="form.notes" rows="3" />
                  </b-form-group>
                </b-col>
              </b-row>

              <div class="text-right">
                <b-button variant="light" class="mr-2" @click="cancel">{{ $t('label.cancel') }}</b-button>
                <b-button type="submit" variant="primary" :disabled="submitting">{{ $t('label.submit') }}</b-button>
              </div>
            </b-form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.tel-input-wrap >>> .vue-tel-input {
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  background: #fff;
}
.tel-input-wrap >>> .vue-tel-input:focus-within {
  border-color: #7c4dff;
  box-shadow: 0 0 0 0.2rem rgba(124, 77, 255, 0.15);
}
.tel-input-wrap >>> .vti__input {
  border: none;
  border-radius: 0 0.25rem 0.25rem 0;
  padding: 0.375rem 0.75rem;
  font-size: 14px;
  color: #495057;
}
.tel-invalid >>> .vue-tel-input {
  border-color: #f46a6a;
}
.ms-invalid >>> .multiselect__tags {
  border-color: #f46a6a;
}
</style>
